/*
  File: src/pages/VendorsManagement.jsx
  Purpose: Manage vendors CRUD with ProductManagement-style UI and UX (simplified)
*/
import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Loader from '../components/common/Loader';
import VendorsAPI from '../api/vendors';

export default function VendorsManagement() {
    function RowsSelect({ value, onChange, options = [10, 20, 50, 100] }) {
        const [isOpen, setIsOpen] = useState(false);
        return (
            <div className="relative min-w-[86px]">
                <div
                    className="w-full px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer min-h-[34px] flex items-center justify-between text-xs sm:text-sm"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="truncate text-white/90">{value}</div>
                    <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
                {isOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-white/10 rounded-lg shadow-lg">
                        <div className="max-h-60 overflow-y-auto">
                            {options.map(opt => (
                                <div
                                    key={opt}
                                    className={`px-3 py-2 cursor-pointer hover:bg-white/10 ${Number(opt) === Number(value) ? 'bg-cyan-600/20 text-cyan-300' : ''}`}
                                    onClick={() => { onChange(Number(opt)); setIsOpen(false); }}
                                >
                                    {opt}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(20);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null); // future-proof, not used now
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [form, setForm] = useState({ name: '' });
    const [search, setSearch] = useState('');

    const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await VendorsAPI.list({ name: search.trim() || undefined, limit, offset });
            setItems(Array.isArray(data.items) ? data.items : []);
            setTotal(Number(data.total || 0));
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Failed to load vendors');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit, offset]);

    // Debounce search typing
    useEffect(() => {
        const t = setTimeout(() => { setOffset(0); load(); }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const resetForm = () => {
        setForm({ name: '' });
    };

    const openCreate = () => { resetForm(); setCreating(true); };

    const submitForm = async (e) => {
        e?.preventDefault?.();
        const payload = { name: form.name };
        try {
            const created = await VendorsAPI.create(payload);
            setItems((arr) => [created, ...arr]);
            setTotal((t) => t + 1);
            setCreating(false);
            resetForm();
        } catch (e) {
            alert(e.response?.data?.message || e.message || 'Failed to save');
        }
    };

    const doDelete = async (it) => {
        try {
            await VendorsAPI.remove(it.id);
            setItems((arr) => arr.filter((x) => x.id !== it.id));
            setTotal((t) => Math.max(0, t - 1));
            setConfirmDelete(null);
        } catch (e) {
            alert(e.response?.data?.message || e.message || 'Failed to delete');
        }
    };

    return (
        <div className="min-h-screen flex flex-col pt-14 sm:pt-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
            <Navbar />
            <main className="flex-1 w-full max-w-5xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2 w-full">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold">Vendors Management</h1>
                        <p className="text-white/70 text-xs sm:text-sm">Create, list and delete vendors</p>
                    </div>
                    <button type="button" onClick={openCreate} className="px-4 py-2 text-sm sm:text-base bg-cyan-600 hover:bg-cyan-500 rounded-lg">New Vendor</button>
                </div>

                <div className="mb-4 flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3 w-full">
                    <input
                        type="text"
                        placeholder="Search by name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-3 py-2 rounded bg-white/5 border border-white/10 text-sm"
                    />
                    <div className="flex items-center gap-2 justify-end">
                        <span className="hidden sm:inline text-white/60 text-xs sm:text-sm">Rows</span>
                        <RowsSelect value={limit} onChange={(v) => { setOffset(0); setLimit(v); }} />
                    </div>
                </div>

                <div className="bg-white/5 backdrop-blur rounded-xl border border-white/10 overflow-x-auto">
                    {loading ? (
                        <div className="py-16"><Loader label="Loading vendors..." /></div>
                    ) : error ? (
                        <div className="p-4 text-red-300">{error}</div>
                    ) : (
                        <div className="min-w-full">
                            <table className="min-w-[500px] w-full text-xs sm:text-sm">
                                <thead className="text-white/70">
                                    <tr>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[56px]">ID</th>
                                        <th className="text-left px-2 sm:px-4 py-2 sm:py-3 min-w-[140px]">Name</th>
                                        <th className="text-right px-2 sm:px-4 py-2 sm:py-3 min-w-[110px]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it) => (
                                        <tr key={it.id} className="border-t border-white/10">
                                            <td className="px-2 sm:px-4 py-2 sm:py-3 align-middle">{it.id}</td>
                                            <td className="px-2 sm:px-4 py-2 sm:py-3 align-middle">{it.name}</td>
                                            <td className="px-2 sm:px-4 py-2 sm:py-3 flex flex-row justify-end gap-1 sm:gap-2 whitespace-nowrap">
                                                <button onClick={() => setConfirmDelete(it)} className="px-2 sm:px-3 py-1 rounded border border-red-400/40 bg-red-600/15 hover:bg-red-600/25 text-red-200 text-xs sm:text-sm">Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {items.length === 0 && (
                                        <tr><td colSpan={3} className="px-2 sm:px-4 py-8 sm:py-10 text-center text-white/60">No vendors</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between text-xs sm:text-sm gap-2 w-full">
                    <div className="text-white/60">Total: {total}</div>
                    <div className="flex items-center gap-2">
                        <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="px-3 py-1 rounded bg-white/5 border border-white/10 disabled:opacity-50">Prev</button>
                        <div className="text-white/70">Page {page} / {totalPages}</div>
                        <button disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} className="px-3 py-1 rounded bg-white/5 border border-white/10 disabled:opacity-50">Next</button>
                    </div>
                </div>
            </main>
            <Footer />

            {creating && (
                <div className="fixed inset-0 z-[2147483646] flex items-center justify-center px-2 sm:px-0">
                    <div className="absolute inset-0 bg-black/60" onClick={() => { setCreating(false); }} />
                    <div className="relative w-full sm:w-[92vw] max-w-md sm:max-w-xl rounded-2xl border border-white/10 bg-gradient-to-br from-[#0a1628]/95 to-[#1a2448]/95 shadow-2xl p-3 sm:p-5 mx-auto">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-base sm:text-lg font-semibold">New Vendor</div>
                            <button type="button" onClick={() => { setCreating(false); }} className="text-white/70 hover:text-white">✕</button>
                        </div>
                        <form onSubmit={submitForm} className="space-y-4">
                            <div>
                                <label className="block text-xs sm:text-sm mb-1">Name</label>
                                <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required className="w-full px-3 py-2 rounded bg-white/5 border border-white/10 text-xs sm:text-sm" />
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 w-full mt-2">
                                <button type="button" onClick={() => { setCreating(false); }} className="px-4 py-2 rounded bg-white/5 border border-white/10 text-sm">Cancel</button>
                                <button type="submit" className="px-4 py-2 rounded bg-cyan-600 hover:bg-cyan-500 text-sm">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-[2147483646] flex items-center justify-center px-2 sm:px-0">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setConfirmDelete(null)} />
                    <div className="relative w-full sm:w-[92vw] max-w-xs sm:max-w-sm rounded-2xl border border-white/10 bg-gradient-to-br from-[#1b1f3a]/95 to-[#222a55]/95 shadow-2xl p-3 sm:p-5 mx-auto">
                        <div className="text-base sm:text-lg font-semibold mb-2">Delete Vendor</div>
                        <p className="text-white/80 text-xs sm:text-sm mb-4">Are you sure you want to delete "{confirmDelete.name}"?</p>
                        <div className="flex flex-col sm:flex-row justify-end gap-2 w-full">
                            <button type="button" onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded bg-white/5 border border-white/10 text-sm">Cancel</button>
                            <button type="button" onClick={() => doDelete(confirmDelete)} className="px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-sm">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


